import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { ROLES, type Role } from "@backstory/protocol";
import { ROLE_LABELS } from "../../auth/roles";
import { OrgLayout } from "../../components/Shell";
import { CopyButton, ConfirmDialog, DataTable, Dialog, Field, PageHeader, RowMenu, Switch, useToast } from "../../components/ui";
import { ErrorState, Skeleton } from "../../components/States";
import { formatDateTime, formatRelative } from "../../lib/format";
import { api, API_URL } from "../../api";
import type { AuditEntry } from "@backstory/protocol";
import { billingKeys, scimApi, teamsApi, type ScimToken, type TeamsInstallation } from "./api";
import { DirtyActions, settingsEqual } from "../../components/UnsavedChanges";

/** Owner is never granted through provisioning. */
const SCIM_ROLES = ROLES.filter((r) => r !== "owner");
export type MapRow = { group: string; role: Role };
export function rowsFromMap(m: Record<string, string>): MapRow[] {
  return Object.entries(m).map(([group, role]) => ({ group, role: (ROLES.includes(role as Role) ? role : "readonly") as Role }));
}
export function mapFromRows(rows: MapRow[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const r of rows) {
    const g = r.group.trim();
    if (g) out[g] = r.role;
  }
  return out;
}

export function ScimPage() {
  const { org: orgId } = useParams({ strict: false }) as { org: string };
  const qc = useQueryClient();
  const toast = useToast();
  const tokens = useQuery({ queryKey: billingKeys.scimTokens(orgId), queryFn: () => scimApi.tokens(orgId), retry: 0 });
  const roles = useQuery({ queryKey: billingKeys.scimRoles(orgId), queryFn: () => scimApi.groupRoles(orgId), retry: 0 });
  const log = useQuery({ queryKey: billingKeys.scimLog(orgId), queryFn: () => api.orgs.audit(orgId, { action: "scim.", limit: 50 }), retry: 0 });
  const teams = useQuery({ queryKey: billingKeys.teams(orgId), queryFn: () => teamsApi.installations(orgId), retry: 0 });
  const [rows, setRows] = useState<MapRow[]>([]);
  const [savedRows, setSavedRows] = useState<MapRow[]>([]);
  const [baseUrl, setBaseUrl] = useState(`${API_URL}/scim/v2`);
  const [revealedToken, setRevealedToken] = useState<string | null>(null);
  const [linkCode, setLinkCode] = useState("");
  const [tokenAction, setTokenAction] = useState<"rotate" | "revoke" | null>(null);
  const [unlinking, setUnlinking] = useState<TeamsInstallation | null>(null);
  useEffect(() => {
    if (roles.data) {
      const next = rowsFromMap(roles.data.roles ?? {});
      // An immediate-save control (such as Enable SCIM) may refetch this query
      // while the mapping editor is dirty. Refresh the baseline without
      // throwing that separate draft away.
      setRows((current) => settingsEqual(mapFromRows(current), mapFromRows(savedRows)) ? next : current);
      setSavedRows(next);
    }
  }, [roles.data]);
  const mappingDirty = !settingsEqual(mapFromRows(rows), mapFromRows(savedRows));

  const activeToken = (tokens.data ?? []).find((t) => !t.revokedAt);
  const saveRoles = useMutation({
    mutationFn: (groupRoleMap: Record<string, string>) => scimApi.putGroupRoles(orgId, groupRoleMap),
    onSuccess: () => {
      setSavedRows(rows);
      toast.push("info", "Group mapping saved.");
      void qc.invalidateQueries({ queryKey: billingKeys.scimRoles(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const reveal = (created: { token: string; baseUrl: string }) => {
    if (created.baseUrl) setBaseUrl(created.baseUrl);
    setRevealedToken(created.token);
    void qc.invalidateQueries({ queryKey: billingKeys.scimTokens(orgId) });
  };
  const createToken = useMutation({
    mutationFn: () => scimApi.createToken(orgId),
    onSuccess: reveal,
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const rotateToken = useMutation({
    mutationFn: (id: string) => scimApi.rotateToken(orgId, id),
    onSuccess: (created) => {
      setTokenAction(null);
      reveal(created);
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const revokeToken = useMutation({
    mutationFn: (id: string) => scimApi.revokeToken(orgId, id),
    onSuccess: () => {
      setTokenAction(null);
      toast.push("info", "SCIM token revoked. Provisioning stops until a new token is issued.");
      void qc.invalidateQueries({ queryKey: billingKeys.scimTokens(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const link = useMutation({
    mutationFn: () => teamsApi.link(orgId, linkCode),
    onSuccess: () => {
      toast.push("info", "Teams channel linked.");
      setLinkCode("");
      void qc.invalidateQueries({ queryKey: billingKeys.teams(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const patchTeams = useMutation({
    mutationFn: (v: { id: string; minSeverity?: string; enabled?: boolean }) => teamsApi.patch(orgId, v.id, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: billingKeys.teams(orgId) }),
    onError: (e) => toast.push("error", (e as Error).message),
  });
  const removeTeams = useMutation({
    mutationFn: (id: string) => teamsApi.remove(orgId, id),
    onSuccess: () => {
      setUnlinking(null);
      toast.push("info", "Teams channel unlinked.");
      void qc.invalidateQueries({ queryKey: billingKeys.teams(orgId) });
    },
    onError: (e) => toast.push("error", (e as Error).message),
  });

  return (
    <OrgLayout orgId={orgId}>
      <PageHeader title="Provisioning (SCIM) & Teams" />

      <div className="card section">
        <h2>SCIM 2.0</h2>
        {tokens.isPending || roles.isPending ? (
          <Skeleton lines={3} />
        ) : tokens.isError || roles.isError ? (
          <ErrorState error={tokens.error ?? roles.error} retry={() => { void tokens.refetch(); void roles.refetch(); }} />
        ) : (
          <>
            <div className="grid2" style={{ marginTop: 12 }}>
              <Field label="SCIM base URL" hint="Paste into your identity provider's SCIM connector.">
                {(p) => <div className="row"><input {...p} className="input mono" readOnly value={baseUrl} /><CopyButton value={baseUrl} /></div>}
              </Field>
              <Field label="Bearer token" hint={activeToken ? `A token is active (${activeToken.prefix}…). Rotating issues a new one and revokes this one.` : "No token issued yet. Provisioning starts once a token is issued."}>
                {() => (
                  <div className="row" style={{ flexWrap: "wrap" }}>
                    <button className="btn primary" onClick={() => (activeToken ? setTokenAction("rotate") : createToken.mutate())} disabled={createToken.isPending || rotateToken.isPending}>{activeToken ? "Rotate token" : "Generate token"}</button>
                    {activeToken && <button className="btn danger" onClick={() => setTokenAction("revoke")} disabled={revokeToken.isPending}>Revoke</button>}
                  </div>
                )}
              </Field>
            </div>
            {(tokens.data ?? []).filter((t) => t.id !== activeToken?.id).length > 0 && (
              <ul className="small" style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                {(tokens.data ?? []).filter((t: ScimToken) => t.revokedAt).slice(0, 3).map((t) => (
                  <li key={t.id} className="muted">{t.name} · {t.prefix}… revoked {formatRelative(t.revokedAt!)}</li>
                ))}
              </ul>
            )}
            <h3 style={{ marginTop: 16 }}>Group → role mapping</h3>
            <p className="muted small">Members of a provisioned group receive the mapped role. Users without a mapped group get {ROLE_LABELS[(roles.data?.defaultRole as Role) || "readonly"] ?? roles.data?.defaultRole}.</p>
            <div className="stack-table">
            <table className="table" aria-label="Group role map">
              <thead><tr><th>IdP group</th><th>Role</th><th></th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} className="static">
                    <td data-label="IdP group"><input className="input sm" aria-label={`Group ${i + 1}`} value={r.group} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, group: e.target.value } : x)))} autoComplete="off" /></td>
                    <td data-label="Role">
                      <select className="input sm" aria-label={`Role for group ${i + 1}`} value={r.role === "owner" ? "admin" : r.role} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, role: e.target.value as Role } : x)))} autoComplete="off">
                        {SCIM_ROLES.map((role) => <option key={role} value={role}>{ROLE_LABELS[role]}</option>)}
                      </select>
                    </td>
                    <td><button className="btn ghost sm" onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label={`Remove mapping ${i + 1}`}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="row" style={{ marginTop: 8 }}>
              <button className="btn" onClick={() => setRows([...rows, { group: "", role: "developer" }])}>Add mapping</button>
              <DirtyActions dirty={mappingDirty} onDiscard={() => setRows(savedRows)}>
                <button className="btn primary" onClick={() => saveRoles.mutate(mapFromRows(rows))} disabled={saveRoles.isPending || !mappingDirty}>Save mapping</button>
              </DirtyActions>
            </div>
          </>
        )}
      </div>

      <div className="card section stack-table" style={{ marginTop: 16 }}>
        <h2>Provisioning log</h2>
        {log.isError ? (
          <ErrorState error={log.error} retry={() => void log.refetch()} />
        ) : (
          <DataTable<AuditEntry>
            ariaLabel="SCIM log"
            rows={log.data?.items ?? []}
            rowKey={(e) => e.id}
            loading={log.isPending}
            empty="No SCIM operations yet."
            columns={[
              { key: "at", header: "When", label: "When", render: (e) => <span title={formatDateTime(e.at)}>{formatRelative(e.at)}</span> },
              { key: "op", header: "Operation", label: "Operation", render: (e) => <span className="mono small">{e.action.replace(/^scim\./, "")}</span> },
              { key: "res", header: "Resource", label: "Resource", render: (e) => (e.target ? `${e.target.type} ${e.target.id}` : "—") },
              { key: "who", header: "Actor", label: "Actor", render: (e) => e.actor.email ?? e.actor.id },
            ]}
          />
        )}
      </div>

      <div className="card section stack-table" style={{ marginTop: 16 }} data-testid="teams-card">
        <h2>Microsoft Teams bot</h2>
        <p className="muted small">Add the Backstory app to a Teams channel; it posts a 6-character code. Enter the code here to link the channel. Cards update in place and support assign, snooze, resolve, and acknowledge.</p>
        {teams.data && !teams.data.configured && <div className="banner warn">The Teams bot is not configured on this deployment.</div>}
        <div className="row" style={{ flexWrap: "wrap" }}>
          <input className="input mono" aria-label="Teams link code" placeholder="ABC123" value={linkCode} onChange={(e) => setLinkCode(e.target.value.toUpperCase())} maxLength={6} style={{ width: 140 }} autoComplete="off" />
          <button className="btn primary" onClick={() => link.mutate()} disabled={linkCode.length < 4 || link.isPending}>Link channel</button>
        </div>
        <DataTable<TeamsInstallation>
          ariaLabel="Teams channels"
          rows={teams.data?.items ?? []}
          rowKey={(i) => i.id}
          loading={teams.isPending}
          empty={<span>No linked channels.</span>}
          columns={[
            { key: "ch", header: "Channel", label: "Channel", render: (i) => <span><strong>{i.channelName ?? i.conversationType}</strong>{i.teamName ? <span className="muted"> · {i.teamName}</span> : null}</span> },
            { key: "sev", header: "Min severity", label: "Min severity", render: (i) => (
              <select className="input sm" aria-label={`Minimum severity for ${i.channelName ?? i.id}`} value={i.minSeverity} onChange={(e) => patchTeams.mutate({ id: i.id, minSeverity: e.target.value })} autoComplete="off">
                {["info", "low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) },
            { key: "on", header: "Enabled", label: "Enabled", render: (i) => <Switch checked={i.enabled} onChange={(v) => patchTeams.mutate({ id: i.id, enabled: v })} label="" /> },
            { key: "linked", header: "Linked", label: "Linked", render: (i) => (i.linkedAt ? formatRelative(i.linkedAt) : "pending") },
            { key: "rm", header: "", render: (i) => (
              <RowMenu label={`Actions for ${i.channelName ?? "channel"}`}>
                <button type="button" role="menuitem" className="danger" onClick={() => setUnlinking(i)}>Unlink</button>
              </RowMenu>
            ) },
          ]}
        />
      </div>

      <ConfirmDialog
        open={tokenAction === "rotate"}
        danger
        title="Rotate SCIM token"
        confirmLabel="Rotate token"
        busy={rotateToken.isPending}
        onClose={() => { if (!rotateToken.isPending) setTokenAction(null); }}
        onConfirm={() => activeToken && rotateToken.mutate(activeToken.id)}
      >
        <p style={{ margin: 0 }}>The current token stops working as soon as the new one is issued. Update your identity provider with the new token.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={tokenAction === "revoke"}
        danger
        title="Revoke SCIM token"
        confirmLabel="Revoke token"
        busy={revokeToken.isPending}
        onClose={() => { if (!revokeToken.isPending) setTokenAction(null); }}
        onConfirm={() => activeToken && revokeToken.mutate(activeToken.id)}
      >
        <p style={{ margin: 0 }}>Provisioning stops until you issue a new token.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={!!unlinking}
        danger
        title="Unlink channel"
        confirmLabel="Unlink channel"
        busy={removeTeams.isPending}
        onClose={() => { if (!removeTeams.isPending) setUnlinking(null); }}
        onConfirm={() => unlinking && removeTeams.mutate(unlinking.id)}
      >
        <p style={{ margin: 0 }}><strong>{unlinking?.channelName ?? "This channel"}</strong> stops receiving cards.</p>
      </ConfirmDialog>
      <Dialog open={revealedToken !== null} onClose={() => setRevealedToken(null)} title="SCIM bearer token" footer={<button className="btn primary" onClick={() => setRevealedToken(null)}>Done</button>}>
        <p>Copy this token into your identity provider now. It is shown once and stored hashed.</p>
        {revealedToken && (
          <div className="secret-row">
            <code className="secret" data-testid="scim-token">{revealedToken}</code>
            <CopyButton value={revealedToken} />
          </div>
        )}
      </Dialog>
    </OrgLayout>
  );
}
